<script setup lang="ts">
import type { ErrorData } from "@anggunpraya-templates/types";
import type { NuxtError } from "#app";

const props = defineProps({
  error: Object as () => NuxtError<ErrorData & {
    redirect?: string;
  }>,
});

const { md } = useBreakpointTailwind();
const { coupleName } = storeToRefs(useTemplateStore());

const errorProps = computed(() => {
  const message = props.error?.data?.message || props.error?.message;
  return {
    status: props.error?.data?.status || props.error?.statusText,
    code: props.error?.data?.code || props.error?.status,
    message: message?.toLowerCase().includes("page not found") ? "Halaman tidak ditemukan" : message || "Terjadi kesalahan",
  };
});

const isPrivateInvitationError = computed(() => {
  return errorProps.value.message === "Mohon maaf, undangan yang Anda akses bersifat pribadi.";
});

watch(
  () => errorProps.value,
  (newError) => {
    if (isPrivateInvitationError.value) {
      useHead({
        title: `Undangan Pribadi - ${coupleName.value}`,
      });
    }
    else {
      useHead({
        title: `${newError.message} - ${coupleName.value}`,
      });
    }
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <div class="flex flex-col h-screen relative overflow-clip">
    <main class="flex-1 size-full flex flex-col items-center justify-center relative gap-2 text-center">
      <!-- Overlay -->
      <div class="bg-[#101010]/25 absolute inset-0 backdrop-blur-xs" />

      <!-- Background Image -->
      <NuxtImg
        v-if="md"
        src="/hero-bg-watercolor-desktop.webp"
        alt="Error"
        class="img-desktop w-full h-full object-cover absolute inset-0 z-[-1]"
        loading="eager"
        decoding="async"
        fetchpriority="high"
        :preload="{
          fetchPriority: 'high',
        }"
      />

      <NuxtImg
        v-else
        src="/hero-bg-watercolor-mobile.webp"
        alt="Error"
        class="img-mobile w-full h-full object-cover absolute inset-0 z-[-1]"
        loading="eager"
        decoding="async"
        fetchpriority="high"
        :preload="{
          fetchPriority: 'high',
        }"
      />

      <UCard
        class="z-1 bg-white/80 ring-0 shadow-md backdrop-[2px] w-full max-w-[calc(100vw-2rem)] xs:max-w-sm sm:max-w-md md:max-w-lg relative overflow-clip"
        :ui="{
          root: 'divide-none',
          header: 'pt-8 pb-6 sm:pt-8 sm:pb-6',
          footer: 'pb-8 pt-6 sm:pb-8 sm:pt-6',
        }"
      >
        <template #header>
          <header class="flex flex-col gap-2">
            <p class="uppercase text-sm xs:text-base">
              PERNIKAHAN
            </p>

            <h1 class="text-primary-800 font-serif text-3xl my-2 md:text-4xl lg:text-5xl">
              {{ coupleName }}
            </h1>
          </header>
        </template>

        <!-- Body -->
        <div class="flex flex-col text-center flex-1 w-full">
          <!-- Opening Card Decoration -->
          <NuxtImg
            src="/opening-card-decoration.webp"
            alt="Dekorasi Kartu Undangan"
            class="absolute inset-0 w-full h-full object-cover opacity-80 -z-1"
            decoding="async"
            loading="eager"
            fetchpriority="high"
            :preload="{
              fetchPriority: 'high',
            }"
          />

          <div class="flex flex-col items-center justify-center gap-4 mx-auto text-sm max-w-md z-1 font-medium md:text-base">
            <p class="max-w-sm">
              {{ errorProps.message }}
            </p>

            <p v-if="isPrivateInvitationError">
              Undangan ini bersifat pribadi dan tidak untuk disebarluaskan. Jika Anda menerima undangan ini, harap konfirmasi kehadiran melalui tautan RSVP yang dikirimkan oleh pengantin kepada Anda.
            </p>
          </div>
        </div>

        <template #footer>
          <NuxtLink
            class="w-auto h-auto inline-flex items-center justify-center"
            href="https://anggunpraya.id"
            target="_blank"
            rel="noopener noreferrer"
            external
          >
            <UiLogoBrand
              variant="dark"
              alt="Anggun Praya"
              class="w-45 h-auto md:w-49 xl:w-52"
            />
          </NuxtLink>
        </template>
      </UCard>
    </main>
  </div>
</template>
